import { useState, useRef, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config/appConfig';
import { track } from '../utils/analytics';
import { toast } from 'sonner';

/**
 * useNutritionGoals — Manages SmartGoals, Intelligence Level, AI Nutrition Goals,
 * and MIQ intake options. Extracted from App.js Phase 5 Heavy Batch Round 1.
 */
export default function useNutritionGoals({ userId, userTier, metabolicProfile, isAuthenticated, authLoading, showUpgrade }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [showAiNutritionGoals, setShowAiNutritionGoals] = useState(false);
  const [aiNutritionGoals, setAiNutritionGoals] = useState(null);
  const [miqIntakeOptions, setMiqIntakeOptions] = useState(null);
  const [smartGoals, setSmartGoals] = useState(null);
  const [smartGoalsLoading, setSmartGoalsLoading] = useState(false);
  const [goalsToast, setGoalsToast] = useState(null);
  const [intelligenceLevel, setIntelligenceLevel] = useState('basic');
  const [previousIntelligenceLevel, setPreviousIntelligenceLevel] = useState('basic');

  // ── Refs (cancellation guards) ─────────────────────────────────────────────
  const smartGoalsAbortRef = useRef(null);
  const smartGoalsOpenRef = useRef(false);
  const smartGoalsLoadingAbortRef = useRef(null);

  // ── Load Smart Goals based on Intelligence Level ───────────────────────────
  const loadSmartGoals = useCallback(async (uid, level, previousLevel = null) => {
    if (!uid) return;

    if (smartGoalsLoadingAbortRef.current) {
      smartGoalsLoadingAbortRef.current.abort();
    }

    const controller = new AbortController();
    smartGoalsLoadingAbortRef.current = controller;

    setSmartGoalsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/nutrition/goals/smart-calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: uid, intelligence_level: level }),
        signal: controller.signal
      });

      if (controller.signal.aborted) return;

      if (response.ok) {
        const data = await response.json();
        if (controller.signal.aborted) return;

        setSmartGoals(data);

        // Show toast if level upgraded
        if (previousLevel && previousLevel !== level) {
          const levelLabels = { basic: 'Basic', personalized: 'Personalized', metabolic: 'AI-Optimized' };
          const levelIcons = { basic: '📊', personalized: '🎯', metabolic: '🧬' };
          setGoalsToast({
            type: 'upgrade',
            icon: levelIcons[level],
            title: `Goals Upgraded to ${levelLabels[level]}!`,
            message: data.explanation,
            level
          });
          setTimeout(() => setGoalsToast(null), 5000);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading smart goals:', error);
    } finally {
      if (!controller.signal.aborted) setSmartGoalsLoading(false);
    }
  }, []);

  // ── Refresh smart goals manually ───────────────────────────────────────────
  const refreshSmartGoals = useCallback(async () => {
    if (userId) {
      await loadSmartGoals(userId, intelligenceLevel);
      setGoalsToast({
        type: 'refresh',
        icon: '✅',
        title: 'Goals Refreshed',
        message: 'Your nutrition targets have been recalculated.',
        level: intelligenceLevel
      });
      setTimeout(() => setGoalsToast(null), 3000);
    }
  }, [userId, intelligenceLevel, loadSmartGoals]);

  // ── Calculate Intelligence Level based on profile completeness ─────────────
  const calculateIntelligenceLevel = useCallback((profile, triggerGoalUpdate = true) => {
    if (!profile) {
      setIntelligenceLevel('basic');
      return 'basic';
    }

    const hasBodyComp = profile.body_composition && profile.body_composition.body_type;
    const hasMetabolicProfile = metabolicProfile?.hasProfile;
    const hasBasicProfile = profile.height_cm && profile.weight_kg && profile.age && profile.gender;

    let newLevel = 'basic';
    if (hasBodyComp && hasMetabolicProfile) {
      newLevel = 'metabolic';
    } else if (hasBasicProfile && hasBodyComp) {
      newLevel = 'personalized';
    } else if (hasBasicProfile) {
      newLevel = 'personalized';
    }

    setIntelligenceLevel(prevLevel => {
      if (triggerGoalUpdate && prevLevel !== newLevel && userId) {
        loadSmartGoals(userId, newLevel, prevLevel);
      }
      setPreviousIntelligenceLevel(prevLevel);
      return newLevel;
    });

    return newLevel;
  }, [metabolicProfile, userId, loadSmartGoals]);

  // ── Load AI Nutrition Goals (with cancellation) ────────────────────────────
  const loadAiNutritionGoals = useCallback(async (uid, forceUpdate = false) => {
    if (smartGoalsAbortRef.current) {
      smartGoalsAbortRef.current.abort();
    }

    const controller = new AbortController();
    smartGoalsAbortRef.current = controller;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${uid}`, {
        signal: controller.signal
      });

      if (controller.signal.aborted) return;

      if (response.ok) {
        const data = await response.json();
        if (!controller.signal.aborted) {
          console.log('[loadAiNutritionGoals] Setting aiNutritionGoals with targets:', data?.nutrition_goals?.daily_targets);
          setAiNutritionGoals(data);
        }
      } else {
        console.error('SmartGoals API returned status:', response.status);
        if (!controller.signal.aborted) {
          setAiNutritionGoals({
            success: false, locked: false, intake_complete: false,
            error: `API returned ${response.status}`
          });
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading SmartGoals:', error);
      if (!controller.signal.aborted) {
        setAiNutritionGoals({
          success: false, locked: false, intake_complete: false,
          error: error.message
        });
      }
    }
  }, []);

  // ── Load MIQ intake options (with cancellation) ────────────────────────────
  const loadMiqIntakeOptions = useCallback(async () => {
    if (smartGoalsAbortRef.current) {
      smartGoalsAbortRef.current.abort();
    }

    const controller = new AbortController();
    smartGoalsAbortRef.current = controller;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/options`, {
        signal: controller.signal
      });

      if (!smartGoalsOpenRef.current || controller.signal.aborted) return;

      if (response.ok) {
        const data = await response.json();
        if (smartGoalsOpenRef.current && !controller.signal.aborted) {
          setMiqIntakeOptions(data);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading MIQ intake options:', error);
    }
  }, []);

  // ── Tier-gated SmartGoals opener ───────────────────────────────────────────
  const openSmartGoalsGated = useCallback((source = 'unknown') => {
    const hasSmartAccess = userTier === 'smart' || userTier === 'enhanced' || userTier === 'genius' || userTier === 'miq';

    if (!hasSmartAccess) {
      track('smartgoals_locked_click', { source, tier: userTier });
      toast.info('SmartGoals requires Smart Plaite', {
        description: 'Upgrade to unlock personalized AI nutrition goals',
        action: {
          label: 'Upgrade',
          onClick: () => showUpgrade?.('smartgoals')
        }
      });
      return;
    }

    track('smartgoals_opened', { source, tier: userTier });
    setShowAiNutritionGoals(true);
  }, [userTier, showUpgrade]);

  // ── Effect: SmartGoals modal tracking — cleanup on close ───────────────────
  useEffect(() => {
    smartGoalsOpenRef.current = showAiNutritionGoals;

    if (!showAiNutritionGoals && userId) {
      if (smartGoalsAbortRef.current) {
        smartGoalsAbortRef.current.abort();
        smartGoalsAbortRef.current = null;
      }
      setSmartGoalsLoading(false);

      // Force refresh nutrition goals to ensure home screen has latest targets
      setTimeout(async () => {
        if (userId) {
          try {
            const response = await fetch(`${BACKEND_URL}/api/ai-nutrition-goals/${userId}`);
            if (response.ok) {
              const data = await response.json();
              setAiNutritionGoals(data);
              console.log('[SmartGoals] Refreshed targets on modal close');
            }
          } catch (err) {
            console.error('[SmartGoals] Error refreshing on close:', err);
          }
        }
      }, 100);
    }

    return () => {
      if (smartGoalsAbortRef.current) {
        smartGoalsAbortRef.current.abort();
      }
    };
  }, [showAiNutritionGoals, userId]);

  // ── Effect: Auto-load MIQ intake options when SmartGoals modal opens ───────
  useEffect(() => {
    if (showAiNutritionGoals && userId && !miqIntakeOptions) {
      loadMiqIntakeOptions();
    }
  }, [showAiNutritionGoals, userId, miqIntakeOptions, loadMiqIntakeOptions]);

  // ── Effect: Show SmartGoals after profile migration ────────────────────────
  useEffect(() => {
    if (isAuthenticated && userId && !authLoading) {
      const shouldShowSmartGoals = localStorage.getItem('show_smartgoals_after_migration');
      if (shouldShowSmartGoals === 'true') {
        localStorage.removeItem('show_smartgoals_after_migration');
        setTimeout(() => {
          toast('Complete your SmartGoals to unlock personalized insights 🧠', {
            action: {
              label: 'Set Up',
              onClick: () => openSmartGoalsGated('button')
            },
            duration: 8000
          });
        }, 2000);
      }
    }
  }, [isAuthenticated, userId, authLoading, openSmartGoalsGated]);

  return {
    showAiNutritionGoals, setShowAiNutritionGoals,
    aiNutritionGoals, setAiNutritionGoals,
    miqIntakeOptions,
    smartGoals,
    smartGoalsLoading, setSmartGoalsLoading,
    goalsToast, setGoalsToast,
    intelligenceLevel,
    previousIntelligenceLevel,
    openSmartGoalsGated,
    loadSmartGoals,
    refreshSmartGoals,
    calculateIntelligenceLevel,
    loadAiNutritionGoals,
    loadMiqIntakeOptions
  };
}
